import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Divider,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  TrendingUp as TrendingUpIcon,
  Storage as StorageIcon,
  AccountTree as AccountTreeIcon,
  VerifiedUser as VerifiedUserIcon,
  Assessment as AssessmentIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  AutoGraph as AutoGraphIcon,
  Policy as PolicyIcon,
  IntegrationInstructions as IntegrationIcon,
} from '@mui/icons-material';

const DataCatalogRoadmap: React.FC = () => {
  // Current state features
  const currentFeatures = [
    {
      category: 'Data Catalog',
      icon: <StorageIcon />,
      features: [
        'Data asset discovery and cataloging',
        'Metadata management and tagging',
        'Search and filtering capabilities',
        'Asset certification workflows',
        'Domain-based organization',
        'Business glossary integration',
      ],
      status: 'Implemented',
      color: '#4caf50',
    },
    {
      category: 'Data Stewardship',
      icon: <VerifiedUserIcon />,
      features: [
        'Data steward assignment',
        'Ownership tracking',
        'Approval workflows',
        'Stewardship dashboards',
        'Role-based access control',
      ],
      status: 'Partial',
      color: '#ff9800',
    },
    {
      category: 'Data Lineage',
      icon: <AccountTreeIcon />,
      features: [
        'Basic lineage visualization',
        'Source-to-target mapping',
        'Transformation tracking',
      ],
      status: 'Basic',
      color: '#2196f3',
    },
    {
      category: 'Data Quality',
      icon: <AssessmentIcon />,
      features: [
        'Quality score calculation',
        'Completeness metrics',
        'Accuracy tracking',
        'Quality dashboards',
        'Threshold-based alerts',
      ],
      status: 'Implemented',
      color: '#4caf50',
    },
  ];

  // Unidatum-inspired enhancements
  const unidatumEnhancements = [
    {
      category: 'AI Governance',
      priority: 'High',
      features: [
        'AI model cataloging and tracking',
        'ML pipeline governance',
        'Model risk assessment',
        'AI ethics compliance monitoring',
        'Automated bias detection',
      ],
      effort: 'Large',
      value: 'Very High',
    },
    {
      category: 'Data Privacy',
      priority: 'High',
      features: [
        'PII/PHI identification and classification',
        'Privacy policy enforcement',
        'GDPR/CCPA compliance automation',
        'Data masking and anonymization',
        'Consent management',
      ],
      effort: 'Large',
      value: 'Very High',
    },
    {
      category: 'Data Quality & Observability',
      priority: 'High',
      features: [
        'No-code quality rule builder',
        'Automated data profiling',
        'Anomaly detection with ML',
        'Real-time quality monitoring',
        'Data health scorecards',
      ],
      effort: 'Medium',
      value: 'High',
    },
    {
      category: 'Advanced Data Lineage',
      priority: 'Medium',
      features: [
        'End-to-end impact analysis',
        'Column-level lineage',
        'Automated lineage discovery',
        'Cross-system lineage tracking',
        'Business lineage visualization',
      ],
      effort: 'Large',
      value: 'High',
    },
    {
      category: 'Data Governance',
      priority: 'Medium',
      features: [
        'Policy management and enforcement',
        'Automated compliance reporting',
        'Risk assessment framework',
        'Audit trail and transparency',
        'Governance workflow automation',
      ],
      effort: 'Medium',
      value: 'High',
    },
    {
      category: 'Data Notebook',
      priority: 'Low',
      features: [
        'Collaborative data exploration',
        'SQL/Python query interface',
        'Visualization builder',
        'Shared analysis workspace',
        'Version control for queries',
      ],
      effort: 'Medium',
      value: 'Medium',
    },
    {
      category: 'Integrations & APIs',
      priority: 'High',
      features: [
        'REST API for all operations',
        'Webhook support for events',
        'Third-party tool integrations',
        'Data pipeline connectors',
        'SSO/SAML authentication',
      ],
      effort: 'Medium',
      value: 'Very High',
    },
  ];

  // Implementation roadmap
  const roadmapPhases = [
    {
      phase: 'Phase 1: Foundation Enhancement',
      timeline: '1-2 Months',
      focus: 'Core Capabilities',
      items: [
        'Enhanced data catalog search with AI-powered suggestions',
        'Advanced filtering and faceted search',
        'Improved metadata management',
        'Basic API development for integrations',
        'Enhanced quality rule builder',
      ],
    },
    {
      phase: 'Phase 2: Governance & Privacy',
      timeline: '2-3 Months',
      focus: 'Compliance & Security',
      items: [
        'PII/PHI classification automation',
        'Privacy policy enforcement engine',
        'GDPR/CCPA compliance workflows',
        'Data masking capabilities',
        'Audit trail enhancements',
      ],
    },
    {
      phase: 'Phase 3: AI & Advanced Analytics',
      timeline: '3-4 Months',
      focus: 'Intelligence & Automation',
      items: [
        'AI model governance framework',
        'ML-powered anomaly detection',
        'Automated data profiling',
        'Predictive quality monitoring',
        'AI ethics compliance tools',
      ],
    },
    {
      phase: 'Phase 4: Enterprise Integration',
      timeline: '4-6 Months',
      focus: 'Scale & Integration',
      items: [
        'Advanced lineage with impact analysis',
        'Cross-system integration framework',
        'Data notebook for collaboration',
        'Enterprise SSO/SAML',
        'Comprehensive API ecosystem',
      ],
    },
  ];

  return (
    <>
      {/* Print Styles */}
      <style>
        {`
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            @page {
              size: letter;
              margin: 0.5in;
            }
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            /* Hide navigation elements */
            nav, .MuiDrawer-root, .MuiAppBar-root {
              display: none !important;
            }
            /* Preserve Grid layout for columns */
            .MuiGrid-container {
              display: flex !important;
              flex-wrap: wrap !important;
            }
            .MuiGrid-item {
              flex-shrink: 0 !important;
            }
            /* 4-column layout for summary cards */
            .MuiGrid-grid-xs-12.MuiGrid-grid-md-3 {
              flex: 0 0 25% !important;
              max-width: 25% !important;
            }
            /* 2-column layout for capability cards */
            .MuiGrid-grid-xs-12.MuiGrid-grid-md-6 {
              flex: 0 0 50% !important;
              max-width: 50% !important;
            }
            /* 3-column layout for benefits cards */
            .MuiGrid-grid-xs-12.MuiGrid-grid-md-4 {
              flex: 0 0 33.333% !important;
              max-width: 33.333% !important;
            }
            /* Ensure colors are preserved */
            .MuiPaper-root, .MuiCard-root, .MuiChip-root, .MuiAlert-root, .MuiButton-root {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            /* Preserve table styling */
            .MuiTable-root {
              page-break-inside: auto !important;
            }
            .MuiTableRow-root {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            /* Prevent page breaks inside cards */
            .MuiCard-root, .MuiPaper-root {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            /* Hide print button */
            .print-button {
              display: none !important;
            }
            /* Remove top spacing from main container */
            .MuiBox-root {
              margin-top: 0 !important;
            }
          }
        `}
      </style>

      <Box sx={{ width: '100%', maxWidth: '1400px', mx: 'auto', mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 700 }}>
              Data Catalog & Governance Roadmap
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Current State Analysis & Future Enhancement Strategy
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            className="print-button"
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
          >
            Print to PDF
          </Button>
        </Box>

        {/* Executive Summary */}
        <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976d2' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
            Executive Summary
          </Typography>
          <Typography variant="body1" paragraph>
            This document provides a comprehensive analysis of our current Data Catalog capabilities and outlines a strategic roadmap for enhancement. Inspired by industry-leading platforms like Unidatum, we identify key areas for improvement across Data Catalog, Data Stewardship, Data Lineage, and Data Quality.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#4caf50', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>4</Typography>
                  <Typography variant="body2">Core Capabilities</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#2196f3', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>7</Typography>
                  <Typography variant="body2">Enhancement Areas</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#ff9800', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>4</Typography>
                  <Typography variant="body2">Implementation Phases</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#9c27b0', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>6</Typography>
                  <Typography variant="body2">Months Timeline</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Current State Assessment */}
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mt: 5, mb: 3 }}>
          Current State: What We Have Now
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {currentFeatures.map((capability, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%', borderLeft: `4px solid ${capability.color}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: capability.color, mr: 2 }}>{capability.icon}</Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {capability.category}
                      </Typography>
                      <Chip
                        label={capability.status}
                        size="small"
                        sx={{
                          bgcolor: capability.color,
                          color: 'white',
                          fontWeight: 600,
                          mt: 0.5,
                        }}
                      />
                    </Box>
                  </Box>
                  <List dense>
                    {capability.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon fontSize="small" sx={{ color: capability.color }} />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Unidatum-Inspired Enhancements */}
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mt: 5, mb: 3 }}>
          Future Enhancements: Unidatum-Inspired Capabilities
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Strategic Vision</AlertTitle>
          Based on Unidatum's industry-leading platform, we've identified 7 key enhancement areas that will transform our data governance capabilities and align with federal requirements.
        </Alert>

        <TableContainer component={Paper} sx={{ mb: 5 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Enhancement Area</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Priority</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Key Features</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Effort</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Business Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {unidatumEnhancements.map((enhancement, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {enhancement.category}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={enhancement.priority}
                      size="small"
                      color={
                        enhancement.priority === 'High'
                          ? 'error'
                          : enhancement.priority === 'Medium'
                          ? 'warning'
                          : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <List dense sx={{ py: 0 }}>
                      {enhancement.features.slice(0, 3).map((feature, idx) => (
                        <ListItem key={idx} sx={{ py: 0, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <RadioButtonUncheckedIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                      {enhancement.features.length > 3 && (
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 3 }}>
                          +{enhancement.features.length - 3} more features
                        </Typography>
                      )}
                    </List>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={enhancement.effort}
                      size="small"
                      variant="outlined"
                      color={
                        enhancement.effort === 'Large'
                          ? 'error'
                          : enhancement.effort === 'Medium'
                          ? 'warning'
                          : 'success'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={enhancement.value}
                      size="small"
                      sx={{
                        bgcolor:
                          enhancement.value === 'Very High'
                            ? '#4caf50'
                            : enhancement.value === 'High'
                            ? '#2196f3'
                            : '#ff9800',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Implementation Roadmap */}
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mt: 5, mb: 3 }}>
          Implementation Roadmap: 6-Month Plan
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {roadmapPhases.map((phase, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: '100%',
                  borderLeft: `6px solid ${
                    index === 0
                      ? '#4caf50'
                      : index === 1
                      ? '#2196f3'
                      : index === 2
                      ? '#ff9800'
                      : '#9c27b0'
                  }`,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {phase.phase}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Focus: {phase.focus}
                      </Typography>
                    </Box>
                    <Chip
                      label={phase.timeline}
                      sx={{
                        bgcolor:
                          index === 0
                            ? '#4caf50'
                            : index === 1
                            ? '#2196f3'
                            : index === 2
                            ? '#ff9800'
                            : '#9c27b0',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <List dense>
                    {phase.items.map((item, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <TrendingUpIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={item}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Key Benefits */}
        <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#f1f8e9', border: '1px solid #aed581' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#558b2f', fontWeight: 600 }}>
            Expected Benefits & ROI
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SpeedIcon sx={{ fontSize: 40, color: '#558b2f', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#558b2f' }}>
                      Efficiency Gains
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="50% faster data discovery" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="70% reduction in compliance time" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="40% improvement in data quality" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SecurityIcon sx={{ fontSize: 40, color: '#558b2f', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#558b2f' }}>
                      Risk Reduction
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Automated PII/PHI protection" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Real-time compliance monitoring" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Proactive risk identification" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AutoGraphIcon sx={{ fontSize: 40, color: '#558b2f', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#558b2f' }}>
                      Strategic Value
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="AI-ready data foundation" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Enhanced decision-making" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Competitive advantage" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Next Steps */}
        <Paper elevation={2} sx={{ p: 4, bgcolor: '#fff3e0', border: '2px solid #ff9800' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#ff9800', fontWeight: 600 }}>
            Recommended Next Steps
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="1. Stakeholder Review & Prioritization"
                secondary="Review this roadmap with key stakeholders and prioritize enhancement areas based on business needs"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="2. Resource Allocation"
                secondary="Identify team members and budget for Phase 1 implementation (1-2 months)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="3. Vendor Evaluation"
                secondary="Evaluate Unidatum and other enterprise data governance platforms for potential partnership or inspiration"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="4. Proof of Concept"
                secondary="Start with high-priority, high-value features (AI Governance, Data Privacy) for quick wins"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="5. Metrics & KPIs"
                secondary="Establish baseline metrics and success criteria for measuring enhancement impact"
              />
            </ListItem>
          </List>
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            USCIS Data Literacy Support Platform | Data Catalog & Governance Roadmap
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Document Date: October 8, 2025 | Version 1.0
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default DataCatalogRoadmap;
